import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, Settings } from 'lucide-react'
import { PROCUREMENT_LOCKED, PROCUREMENT_STATUS_LABELS, erfuelltRecht, geldText } from '@symphenia/shared-types'
import { DirektLieferant, DirektPreis } from '@/components/list/DirektFeld'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import { ExportButton } from '@/components/ui/ExportButton'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { api } from '@/lib/axios'
import { inlineInput, pageWide } from '@/lib/form-styles'
import { mitStand, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { useTerminology, type Terms } from '@/lib/useTerminology'
import {
  approveBomLines,
  assignSupplier,
  copyBomLines,
  fetchBomCategories,
  fetchPurchasing,
  type BomCategory,
  purchasingExportRequest,
  searchSuppliers,
  setDue,
  type CreateDocumentsResult,
  type PurchaseDocumentKind,
  type BomLine,
  type PurchasingConfirmationFilter,
  type PurchasingFilter,
  type PurchasingLine,
  type PurchasingResult,
} from '../api/project-bom.api'
import { PurchaseDocumentsDialog } from '../components/PurchaseDocumentsDialog'
import { alsTag } from '../project-labels'
import { AngebotsMarke, BestaetigungsMarke, EinkaufsKontext, EinkaufsMarke, type EinkaufsAktionen } from '../components/EinkaufsMarke'
import { ApplyOffersDialog, OfferComparisonTab } from '../components/OfferComparisonTab'

/** `inlineInput` ohne `w-full` — sonst überstimmt es jede gesetzte Breite. */
const schmal = inlineInput.replace('w-full', '')

/**
 * Nachtrag 16 (Auftraggeber 13.10.2026): **Einkaufspreis und Lieferant direkt in der Liste ändern** — solange die
 * Zeile nicht bestellt ist. Dieselbe Speicherstelle wie die Zeile in der Stückliste (`bom:<id>`), derselbe PATCH;
 * der Server hält die Sperre der bestellten Zeile ohnehin (409 `BOM_ORDERED_LOCKED`).
 */
function useEinkaufStelle(z: PurchasingLine, onGeaendert: () => void): Speicherstelle<unknown> {
  return useSpeicherstelle<Datensatz>({
    schluessel: `bom:${z.id}`,
    bezeichnung: z.name,
    daten: z as unknown as Datensatz,
    // Der Lieferant kommt als Objekt, geschrieben wird seine Kennung (wie in der Stückliste).
    werte: (zeile) => ({ ...zeile, supplierId: (zeile.supplier as { id?: string } | null)?.id ?? null }),
    schreibe: async (patch, stand) => {
      const antwort = await api.patch<{ data: BomLine }>(`/projects/bom/${z.id}`, patch, mitStand(stand))
      onGeaendert()
      return antwort.data.data as unknown as Datensatz
    },
    keepalive: { url: `/projects/bom/${z.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
}

function EkZelle({ z, onGeaendert }: { z: PurchasingLine; onGeaendert: () => void }) {
  const stelle = useEinkaufStelle(z, onGeaendert)
  return <DirektPreis stelle={stelle} feld="purchasePrice" label="Einkaufspreis" stellen={4} />
}

function LieferantZelle({ z, onGeaendert }: { z: PurchasingLine; onGeaendert: () => void }) {
  const stelle = useEinkaufStelle(z, onGeaendert)
  return <DirektLieferant stelle={stelle} name={z.supplier?.name ?? null} suche={searchSuppliers} />
}

type Reiter = 'open' | 'ordered' | 'overdue'
/** AP-39 10.4: der Preisspiegel ist kein Filter der Liste, sondern eine eigene Ansicht. */
type Ansicht = Reiter | 'offers'
type Gruppierung = 'supplier' | 'project' | 'due'

/** AP-35 N17-12: Stand der Auftragsbestätigung — nur an bestellten Zeilen, darum nur in „Bestellt" und „Überfällig". */
const BESTAETIGUNG_FELD: FilterFieldDef = {
  key: 'confirmation',
  label: 'Auftragsbestätigung',
  type: 'select',
  category: 'core',
  operators: ['is'],
  options: [
    { value: 'MISSING', label: 'fehlt' },
    { value: 'DEVIATION', label: 'mit Abweichung' },
    { value: 'CONFIRMED', label: 'bestätigt' },
  ],
}

// Eine Funktion, keine Konstante: Die Begriffe des Mandanten und die Kategorien
// stehen erst zur Laufzeit fest (terminologie.md).
const filterFelder = (terms: Terms, kategorien: BomCategory[], reiter: Reiter): FilterFieldDef[] => [
  { key: 'search', label: 'Suche', type: 'text', category: 'core', operators: ['contains'] },
  { key: 'projectSearch', label: terms.project, type: 'text', category: 'core', operators: ['contains'] },
  { key: 'positionSearch', label: terms.position, type: 'text', category: 'core', operators: ['contains'] },
  { key: 'clientName', label: 'Kunde', type: 'text', category: 'core', operators: ['contains'] },
  {
    key: 'categoryId',
    label: 'Kategorie',
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: kategorien.filter((k) => k.isActive).map((k) => ({ value: k.id, label: k.name })),
  },
  { key: 'dueTo', label: 'Liefertermin bis', type: 'date', category: 'core', operators: ['is'] },
  {
    key: 'approval',
    label: 'Bestellfreigabe',
    type: 'select',
    category: 'core',
    operators: ['is'],
    options: [
      { value: 'missing', label: 'fehlt' },
      { value: 'granted', label: 'erteilt' },
    ],
  },
  { key: 'requiresCertificate', label: 'Zeugnispflicht', type: 'boolean', category: 'core', operators: ['is'] },
  { key: 'includePlanned', label: 'auch Positionen in Vorbereitung', type: 'boolean', category: 'core', operators: ['is'] },
  ...(reiter === 'open' ? [] : [BESTAETIGUNG_FELD]),
]

interface Spalte {
  key: string
  label: string
  rechts?: boolean
  preis?: boolean
}

const COLUMN_LAYOUT_KEY = 'columnbar_layout_purchasing'
const STANDARD = ['project', 'position', 'name', 'quantity', 'altQuantity', 'supplier', 'due', 'status', 'approval', 'price', 'total']

function ladeSpalten(): string[] {
  const g = loadUiSetting<{ columnKeys?: string[] } | null>(COLUMN_LAYOUT_KEY, null)
  return g?.columnKeys?.length ? g.columnKeys : STANDARD
}

const zahl = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString(navigator.language, { maximumFractionDigits: 3 }))
/** Betrag im Geldformat (`geld.ts`); leer → „—". */
const geld = (n: number | null | undefined) => geldText(n) || '—'

/**
 * AP-35 Detailblatt 5 5.3 — **Einkaufs-Cockpit** (`/purchasing`).
 *
 * Der Ort des Einkäufers: alles, was zu beschaffen ist, projektübergreifend,
 * nach Lieferant gruppiert. Hier werden Preisanfragen und Bestellungen
 * **erzeugt** — als Entwurf je Lieferant; die Stückliste selbst kopiert nur.
 *
 * Nachtrag 13: Ein Dialog für beide Belegarten (`PurchaseDocumentsDialog`). Entsteht genau
 * ein Beleg, öffnet er sich danach; mehrere stehen als Verweise über der Liste.
 */
export default function PurchasingPage() {
  const { t } = useTranslation()
  const terms = useTerminology()
  const navigate = useNavigate()
  const { user, hasModule } = useAuthStore()
  const darf = (r: string) => erfuelltRecht(r, user?.permissions)
  const preise = darf('projects.price.view')

  const ALLE: Spalte[] = [
    { key: 'project', label: terms.project },
    { key: 'position', label: terms.position },
    { key: 'name', label: 'Bezeichnung' },
    { key: 'quantity', label: 'Bedarf → Bestellung', rechts: true },
    { key: 'altQuantity', label: 'Alternativmenge', rechts: true },
    { key: 'supplier', label: 'Lieferant' },
    { key: 'due', label: 'Liefertermin' },
    { key: 'status', label: 'Status' },
    // AP-35 N17-12: wählbar — ohne die Spalte steht die Bestätigung unter dem Status.
    { key: 'confirmation', label: 'Auftragsbestätigung' },
    { key: 'approval', label: 'Freigabe' },
    { key: 'category', label: 'Kategorie' },
    { key: 'certificate', label: 'Zeugnis' },
    { key: 'price', label: 'EK', rechts: true, preis: true },
    { key: 'total', label: 'Gesamt', rechts: true, preis: true },
  ]

  // N17-12: `?tab=ordered&projectSearch=…&confirmation=MISSING` — der Verweis aus der Stückliste.
  const [suchParameter] = useSearchParams()
  const [ansicht, setAnsicht] = useState<Ansicht>(() => {
    const tab = suchParameter.get('tab')
    return tab === 'ordered' || tab === 'overdue' || tab === 'offers' ? tab : 'open'
  })
  const reiter: Reiter = ansicht === 'offers' ? 'open' : ansicht
  const [adresse] = useState(() => filterAusAdresse(suchParameter, filterFelder(terms, [], reiter)))
  const [gruppe, setGruppe] = useState<Gruppierung>('supplier')
  const [filterWerte, setFilterWerte] = useState<Record<string, ActiveFilter>>({})
  const [zaehler, setZaehler] = useState<PurchasingResult['counts'] | null>(null)
  const [vorschlag, setVorschlag] = useState<{ choices: { lineId: string; documentLineId: string }[]; stands: { lineId: string; updatedAt: string }[] } | null>(null)
  const [spalten, setSpalten] = useState<string[]>(ladeSpalten)
  const [konfig, setKonfig] = useState(false)
  const [zeilen, setZeilen] = useState<PurchasingLine[]>([])
  const [erzeugen, setErzeugen] = useState(false)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [ergebnis, setErgebnis] = useState<{ art: PurchaseDocumentKind; created: CreateDocumentsResult['created'] } | null>(null)
  const [laedt, setLaedt] = useState(false)
  const [lieferantText, setLieferantText] = useState('')
  const [lieferanten, setLieferanten] = useState<{ id: string; name: string }[]>([])
  const [termin, setTermin] = useState('')
  const [kategorien, setKategorien] = useState<BomCategory[]>([])
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const felder = useMemo(() => filterFelder(terms, kategorien, reiter), [terms, kategorien, reiter])

  useEffect(() => {
    // Ohne Kategorien bleibt der Filter nur leer — die Liste lädt trotzdem.
    fetchBomCategories().then(setKategorien).catch(() => setKategorien([]))
  }, [])

  const filter = useCallback(
    (werte: Record<string, ActiveFilter>): PurchasingFilter => {
      const f: PurchasingFilter = { tab: reiter }
      for (const [k, a] of Object.entries(werte)) {
        if (!a.value) continue
        if (k === 'search') f.search = a.value
        if (k === 'projectSearch') f.projectSearch = a.value
        if (k === 'positionSearch') f.positionSearch = a.value
        if (k === 'clientName') f.clientName = a.value
        if (k === 'categoryId') f.categoryId = a.value
        if (k === 'dueTo') f.dueTo = a.value.slice(0, 10)
        if (k === 'approval') f.approval = a.value as 'missing' | 'granted'
        if (k === 'requiresCertificate') f.requiresCertificate = a.value === 'true'
        if (k === 'includePlanned') f.includePlanned = a.value === 'true'
        // Offene Zeilen haben keine Bestätigung — im Reiter „Offen" filtert das Feld nicht (es steht dort auch nicht).
        if (k === 'confirmation' && reiter !== 'open') f.confirmation = a.value as PurchasingConfirmationFilter
      }
      return f
    },
    [reiter],
  )

  const laden = useCallback(
    // `markierungBehalten`: nach einer Änderung direkt in der Liste — was markiert war, bleibt es.
    async (werte: Record<string, ActiveFilter>, markierungBehalten = false) => {
      setLaedt(true)
      try {
        const r = await fetchPurchasing(filter(werte))
        setZeilen(r.data)
        setZaehler(r.counts ?? null)
        setMarkiert((m) => (markierungBehalten ? new Set(r.data.filter((z) => m.has(z.id)).map((z) => z.id)) : new Set()))
        setFehler(null)
      } catch (e) {
        setFehler(apiErrorText(e, 'Die Liste konnte nicht geladen werden.'))
      } finally {
        setLaedt(false)
      }
    },
    [filter],
  )

  useEffect(() => {
    if (ansicht !== 'offers') void laden(filterWerte)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ansicht])

  useEffect(() => {
    if (lieferantText.trim().length < 2) return setLieferanten([])
    const x = setTimeout(() => searchSuppliers(lieferantText.trim()).then(setLieferanten).catch(() => setLieferanten([])), 250)
    return () => clearTimeout(x)
  }, [lieferantText])

  const gezeigt = spalten.map((k) => ALLE.find((c) => c.key === k)).filter((c): c is Spalte => !!c && (!c.preis || preise))
  const hinzu = ALLE.filter((c) => !spalten.includes(c.key) && (!c.preis || preise))
  const auswahl = [...markiert]

  const gruppen = useMemo(() => {
    const m = new Map<string, { titel: string; zeilen: PurchasingLine[] }>()
    for (const z of zeilen) {
      const [k, titel] =
        gruppe === 'supplier'
          ? [z.supplier?.id ?? '-', z.supplier?.name ?? 'ohne Lieferant']
          : gruppe === 'project'
            ? [z.project.id, `${z.project.projectNumber} ${z.project.name}`]
            : [z.deliveryDueAt ?? '-', z.deliveryDueAt ? alsTag(z.deliveryDueAt) : 'ohne Termin']
      const g = m.get(k) ?? { titel, zeilen: [] }
      g.zeilen.push(z)
      m.set(k, g)
    }
    return [...m.values()]
  }, [zeilen, gruppe])

  async function tu(aktion: () => Promise<unknown>, erfolg: string) {
    setFehler(null)
    setHinweis(null)
    setErgebnis(null)
    try {
      await aktion()
      setHinweis(erfolg)
      await laden(filterWerte)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  /** Direkt in der Liste ändern: mit dem Recht an der Stückliste, und nur solange nicht bestellt ist. */
  const darfPflegen = darf('projects.bom.manage')
  const aenderbar = (z: PurchasingLine) => darfPflegen && !PROCUREMENT_LOCKED.includes(z.procurementStatus) && z.procurementStatus !== 'CANCELLED'
  const nachAenderung = () => void laden(filterWerte, true)

  // AP-35 N17-1: „845,00 übernehmen" an der Zeile — dieselbe Vorschau wie im Preisspiegel, der Text führt dorthin.
  const einkauf = useMemo<EinkaufsAktionen>(
    () => ({
      uebernehmen:
        darfPflegen && preise
          ? (z: BomLine) => {
              if (!z.offer?.best) return
              setVorschlag({ choices: [{ lineId: z.id, documentLineId: z.offer.best.documentLineId }], stands: z.updatedAt ? [{ lineId: z.id, updatedAt: z.updatedAt }] : [] })
            }
          : undefined,
      vergleichen: preise ? () => setAnsicht('offers') : undefined,
      oeffneBeleg: (id: string) => navigate(`/correspondence/${id}`),
    }),
    [darfPflegen, preise, navigate],
  )

  /** N17-12: Ein Klick auf den Zähler setzt den Filter „Auftragsbestätigung". */
  const filtereBestaetigung = (wert: PurchasingConfirmationFilter) => {
    const w = { ...filterWerte, confirmation: { fieldKey: 'confirmation', operator: 'is' as const, value: wert } }
    setFilterWerte(w)
    void laden(w)
  }

  const zelle = (z: PurchasingLine, k: string): React.ReactNode => {
    switch (k) {
      case 'project':
        return (
          <button type="button" className="text-primary hover:underline" onClick={() => navigate(`/projects/${z.project.id}?tab=positions&position=${z.position.id}`)}>
            {z.project.projectNumber}
          </button>
        )
      case 'position':
        return z.position.label
      case 'name':
        return (
          <>
            <span className="font-medium">{z.name}</span>
            {z.material && <span className="block text-xs text-muted-foreground">{z.material}</span>}
          </>
        )
      case 'quantity':
        return `${zahl(z.requirement)} → ${zahl(z.orderSuggestion)} ${z.unit?.code ?? ''}`
      case 'altQuantity':
        return z.altQuantity === null ? '—' : `${zahl(z.altQuantity)} ${z.altUnit?.code ?? ''}`
      case 'supplier':
        // Ohne Einkauf (Lager, beigestellt) hat die Zeile keinen Lieferanten zu wählen.
        return aenderbar(z) && z.procurementSource === 'PURCHASE' ? (
          <LieferantZelle z={z} onGeaendert={nachAenderung} />
        ) : (
          (z.supplier?.name ?? <span className="text-muted-foreground">—</span>)
        )
      case 'due':
        return <span className={z.overdue ? 'font-medium text-destructive' : ''}>{alsTag(z.deliveryDueAt)}</span>
      case 'status':
        return (
          <>
            {PROCUREMENT_STATUS_LABELS[z.procurementStatus]}
            {z.inDrafts.length > 0 && <span className="block text-xs text-muted-foreground">im Entwurf {z.inDrafts.join(', ')}</span>}
            {z.orderDocument?.number && <span className="block text-xs text-muted-foreground">{z.orderDocument.number}</span>}
            {/* AP-39 10.4, AP-35 N17-1/N17-10: Antwort und Bestätigung stehen am Beleg — hier die Marke. Steht die
                Spalte „Auftragsbestätigung" daneben, sagt sie die Bestätigung. */}
            <span className="block text-xs">{spalten.includes('confirmation') ? <AngebotsMarke z={z} /> : <EinkaufsMarke z={z} />}</span>
          </>
        )
      case 'confirmation':
        return (
          <span className="text-xs">
            <BestaetigungsMarke z={z} />
          </span>
        )
      case 'approval':
        return z.approvals.order ? 'erteilt' : z.orderApprovalMissing ? <span className="text-amber-700 dark:text-amber-400">fehlt</span> : '—'
      case 'category':
        return z.category?.name ?? '—'
      case 'certificate':
        return z.certificateState === 'none' ? '—' : z.certificateState === 'ok' ? 'vorhanden' : <span className="text-destructive">fehlt</span>
      case 'price':
        return aenderbar(z) ? <EkZelle z={z} onGeaendert={nachAenderung} /> : geld(z.purchasePrice)
      case 'total':
        return geld(z.purchaseTotal)
      default:
        return null
    }
  }

  const spaltenAendern = (keys: string[]) => {
    setSpalten(keys)
    saveUiSetting(COLUMN_LAYOUT_KEY, { columnKeys: keys, columnWidths: {} })
  }

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Einkauf</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Alle Stücklistenzeilen, die zu beschaffen sind — über alle {terms.projectPlural}. Preisanfragen und Bestellungen entstehen hier als Entwurf je Lieferant.
          </p>
        </div>
        <ExportButton
          request={(format, orientation) =>
            purchasingExportRequest({ ...filter(filterWerte), format, orientation }, gezeigt.map((c) => ({ key: c.key === 'price' ? 'ekPrice' : c.key === 'total' ? 'ekTotal' : c.key, label: c.label })))
          }
          mail={{ source: 'EXPORT_PURCHASING', title: t('mailExport.titles.EXPORT_PURCHASING'), subject: t('mailExport.titles.EXPORT_PURCHASING') }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1 border-b border-border">
        {(
          [
            ['open', 'Offen'],
            ['ordered', 'Bestellt'],
            ['overdue', 'Überfällig'],
            // Preise sind Kosten — ohne das Preisrecht gibt es den Reiter nicht.
            ...(preise ? ([['offers', 'Preisspiegel']] as const) : []),
          ] as const
        ).map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setAnsicht(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${ansicht === k ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {l}
          </button>
        ))}
        {ansicht !== 'offers' && (
          <label className="ml-auto flex items-center gap-2 pb-1 text-xs text-muted-foreground">
            gruppiert nach
            <select className={`${schmal} w-36`} value={gruppe} onChange={(e) => setGruppe(e.target.value as Gruppierung)}>
              <option value="supplier">Lieferant</option>
              <option value="project">{terms.project}</option>
              <option value="due">Liefertermin</option>
            </select>
          </label>
        )}
      </div>

      {ansicht === 'offers' && <OfferComparisonTab />}

      {ansicht !== 'offers' && (
        <>
          <FilterBar
            availableFields={felder}
            value={filterWerte}
            onChange={(w) => {
              setFilterWerte(w)
              if (timer.current) clearTimeout(timer.current)
              timer.current = setTimeout(() => void laden(w), 400)
            }}
            layoutKey="purchasing"
            defaultFieldKeys={['search', 'projectSearch', 'positionSearch', 'clientName', 'categoryId', 'approval']}
            adresse={adresse}
          />

          {/* N17-12: über den bestellten Zeilen — gezählt vor dem Filter „Auftragsbestätigung". */}
          {reiter !== 'open' && zaehler && zaehler.ordered > 0 && (
            <div className="flex flex-wrap items-center gap-x-1.5 text-sm" data-bestaetigungszaehler>
              <span className="text-muted-foreground">{zaehler.ordered} bestellt</span>
              {zaehler.missing > 0 && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <button type="button" className="text-amber-700 hover:underline dark:text-amber-400" onClick={() => filtereBestaetigung('MISSING')}>
                    {zaehler.missing} ohne AB
                  </button>
                </>
              )}
              {zaehler.deviation > 0 && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <button type="button" className="font-medium text-destructive hover:underline" onClick={() => filtereBestaetigung('DEVIATION')}>
                    {zaehler.deviation} mit Abweichung
                  </button>
                </>
              )}
            </div>
          )}

          {auswahl.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              <span className="text-muted-foreground">{auswahl.length} markiert</span>
              {darf('projects.approve.procurement') && (
                <button type="button" className="rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => void tu(() => approveBomLines(auswahl, 'ORDER'), 'Bestellfreigabe erteilt.')}>
                  Bestellfreigabe
                </button>
              )}
              <span className="relative">
                <input className={`${schmal} w-44`} placeholder="Lieferant zuweisen …" value={lieferantText} onChange={(e) => setLieferantText(e.target.value)} />
                {lieferanten.length > 0 && (
                  <span className="absolute z-50 mt-1 block max-h-40 w-56 overflow-auto rounded-md border border-border shadow-lg" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
                    {lieferanten.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        className="block w-full px-2 py-1 text-left hover:bg-muted"
                        onClick={() => {
                          setLieferantText('')
                          setLieferanten([])
                          void tu(() => assignSupplier(auswahl, l.id), `Lieferant ${l.name} zugewiesen.`)
                        }}
                      >
                        {l.name}
                      </button>
                    ))}
                  </span>
                )}
              </span>
              <input type="date" className={`${schmal} w-40`} value={termin} onChange={(e) => setTermin(e.target.value)} aria-label="Liefertermin" />
              <button type="button" disabled={!termin} className="rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50" onClick={() => void tu(() => setDue(auswahl, termin), 'Liefertermin gesetzt.')}>
                Termin setzen
              </button>
              <button type="button" className="rounded border border-border px-2 py-1 hover:bg-muted" onClick={() => void tu(() => copyBomLines(auswahl), 'In die Zwischenablage kopiert.')}>
                Kopieren
              </button>
              {hasModule('CORRESPONDENCE') && reiter !== 'ordered' && (
                <button type="button" className="ml-auto rounded-md bg-primary px-3 py-1 font-medium text-primary-foreground" onClick={() => setErzeugen(true)}>
                  Preisanfrage / Bestellung erzeugen …
                </button>
              )}
            </div>
          )}

          {fehler && <p className="text-sm text-destructive">{fehler}</p>}
          {hinweis && <p className="text-sm text-green-700 dark:text-green-400">{hinweis}</p>}
          {ergebnis && (
            <div className="flex items-start justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
              <div className="space-y-1">
                <div>
                  {ergebnis.created.length} {ergebnis.art === 'INQUIRY' ? (ergebnis.created.length === 1 ? 'Preisanfrage' : 'Preisanfragen') : ergebnis.created.length === 1 ? 'Bestellung' : 'Bestellungen'} im
                  Entwurf angelegt:{' '}
                  {ergebnis.created.map((c, i) => (
                    <span key={c.documentId}>
                      {i > 0 && ', '}
                      <Link to={`/correspondence/${c.documentId}`} className="text-primary hover:underline">
                        {c.documentNumber ?? 'Entwurf'} {c.supplierName}
                      </Link>
                    </span>
                  ))}
                </div>
                {ergebnis.created
                  .flatMap((c) => c.skippedFiles.map((f) => ({ ...f, beleg: c.supplierName })))
                  .map((f, i) => (
                    <div key={i} className="text-xs text-amber-700 dark:text-amber-300">
                      {f.fileName} nicht mitgesendet ({f.beleg}): {f.reason === 'TYPE' ? 'Dateityp' : f.reason === 'TOO_MANY' ? 'mehr als 10 Dateien an der Zeile' : 'zu groß oder nicht lesbar'}
                    </div>
                  ))}
              </div>
              <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setErgebnis(null)} aria-label="Hinweis schließen">
                ✕
              </button>
            </div>
          )}

          <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
            <button
              type="button"
              onClick={() => setKonfig((v) => !v)}
              className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md ${konfig ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}
              title="Spalten konfigurieren"
            >
              {konfig ? <Check size={14} /> : <Settings size={14} />}
            </button>
            <HelpPopover topic="columns" className="absolute right-8 top-1.5" />
            {konfig && (
              <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-3 py-2 pr-16 text-xs">
                <LayoutDefaultActions
                  settingKey={COLUMN_LAYOUT_KEY}
                  currentValue={{ columnKeys: spalten, columnWidths: {} }}
                  onReset={(v) => {
                    const keys = (v as { columnKeys?: string[] } | null)?.columnKeys ?? STANDARD
                    markUiSettingApplied(COLUMN_LAYOUT_KEY, { columnKeys: keys, columnWidths: {} })
                    setSpalten(keys)
                  }}
                />
                {hinzu.length > 0 && (
                  <select
                    className="rounded border border-border bg-background px-1 py-0.5"
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) spaltenAendern([...spalten, e.target.value])
                      e.target.value = ''
                    }}
                  >
                    <option value="">+ Spalte</option>
                    {hinzu.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="w-8 px-2" />
                    {gezeigt.map((c) => (
                      <th key={c.key} className={`px-3 py-1.5 font-medium ${c.rechts ? 'text-right' : 'text-left'}`}>
                        {c.label}
                        {konfig && (
                          <button type="button" className="ml-1 text-muted-foreground hover:text-destructive" onClick={() => spaltenAendern(spalten.filter((k) => k !== c.key))}>
                            ✕
                          </button>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!laedt && zeilen.length === 0 && (
                    <tr>
                      <td colSpan={gezeigt.length + 1} className="px-4 py-8 text-center text-muted-foreground">
                        Nichts zu beschaffen.
                      </td>
                    </tr>
                  )}
                  {/* Die Handlungen der Marke (N17-1) — der Kontext rendert nichts, er steht darum auch im tbody richtig. */}
                  <EinkaufsKontext.Provider value={einkauf}>
                    {gruppen.map((g) => (
                      <GruppenZeilen
                        key={g.titel}
                        titel={g.titel}
                        zeilen={g.zeilen}
                        gezeigt={gezeigt}
                        markiert={markiert}
                        setMarkiert={setMarkiert}
                        zelle={zelle}
                      />
                    ))}
                  </EinkaufsKontext.Provider>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {erzeugen && (
        <PurchaseDocumentsDialog
          lineIds={auswahl}
          onClose={() => setErzeugen(false)}
          onDone={(r, art) => {
            setErzeugen(false)
            setHinweis(null)
            // N13-3: Ein einzelner Beleg öffnet sich. Blieb eine Datei unerwartet zurück, steht das
            // hier — im Beleg sähe es niemand.
            const einer = r.created.length === 1 ? r.created[0] : null
            if (einer && !einer.skippedFiles.some((f) => f.reason === 'FAILED')) {
              navigate(`/correspondence/${einer.documentId}`)
              return
            }
            setErgebnis(r.created.length ? { art, created: r.created } : null)
            void laden(filterWerte)
          }}
        />
      )}
      {vorschlag && (
        <ApplyOffersDialog
          choices={vorschlag.choices}
          stands={vorschlag.stands}
          onClose={() => setVorschlag(null)}
          onFertig={(r) => {
            setVorschlag(null)
            setHinweis(r.applied ? `${r.applied === 1 ? '1 Preis' : `${r.applied} Preise`} aus dem Angebot übernommen.` : 'Nichts übernommen — die Zeile hat sich inzwischen geändert.')
            void laden(filterWerte, true)
          }}
        />
      )}
    </div>
  )
}

function GruppenZeilen({
  titel,
  zeilen,
  gezeigt,
  markiert,
  setMarkiert,
  zelle,
}: {
  titel: string
  zeilen: PurchasingLine[]
  gezeigt: Spalte[]
  markiert: Set<string>
  setMarkiert: React.Dispatch<React.SetStateAction<Set<string>>>
  zelle: (z: PurchasingLine, k: string) => React.ReactNode
}) {
  const alle = zeilen.every((z) => markiert.has(z.id))
  return (
    <>
      <tr className="border-b border-border bg-muted/30">
        <td className="px-2 py-1">
          <input
            type="checkbox"
            aria-label={`${titel} markieren`}
            checked={alle}
            onChange={() =>
              setMarkiert((m) => {
                const n = new Set(m)
                for (const z of zeilen) if (alle) n.delete(z.id)
                else n.add(z.id)
                return n
              })
            }
          />
        </td>
        <td colSpan={gezeigt.length} className="px-3 py-1 text-xs font-semibold">
          {titel} · {zeilen.length}
        </td>
      </tr>
      {zeilen.map((z) => (
        <tr key={z.id} className="border-b border-border last:border-0 hover:bg-accent/30">
          <td className="px-2 py-1.5">
            <input
              type="checkbox"
              aria-label={`${z.name} markieren`}
              checked={markiert.has(z.id)}
              onChange={() =>
                setMarkiert((m) => {
                  const n = new Set(m)
                  if (n.has(z.id)) n.delete(z.id)
                  else n.add(z.id)
                  return n
                })
              }
            />
          </td>
          {gezeigt.map((c) => (
            <td key={c.key} className={`px-3 py-1.5 ${c.rechts ? 'whitespace-nowrap text-right tabular-nums' : ''}`}>
              {zelle(z, c.key)}
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}
